import { useMemo } from 'react';
import { CircleUser, Cpu, Swords, Repeat, Trophy, MessageSquare, Lock, type LucideIcon } from 'lucide-react';
import { useT } from '@/i18n';
import { useGameStore } from '@/state/gameStore';
import './BottomNav.css';

export type Tab = 'hero' | 'terminal' | 'dungeon' | 'market' | 'feedback' | 'leaderboard';

interface BottomNavProps {
  active: Tab;
  onChange: (tab: Tab) => void;
}

interface NavItem {
  tab: Tab;
  label: string;
  Icon: LucideIcon;
  /** Minimum player level to unlock — undefined / 0 means always available.
   *  Locked tabs render greyed-out with a lock-icon overlay so the player
   *  sees the progression path without being able to enter the screen. */
  minLevel?: number;
}

// Language-agnostic min-level lookup — used by isTabUnlocked() which is a
// pure helper exported for App.tsx routing-guards and can't run hooks.
const MIN_LEVELS: Partial<Record<Tab, number>> = {
  terminal:    3,
  leaderboard: 5,
  market:      10,
  feedback:    15,
};

export function BottomNav({ active, onChange }: BottomNavProps) {
  const playerLevel = useGameStore((s) => s.player?.level ?? 1);
  const t = useT();

  const ITEMS = useMemo<NavItem[]>(() => [
    { tab: 'hero',        label: t.nav.hero,         Icon: CircleUser },
    { tab: 'dungeon',     label: t.nav.fight,        Icon: Swords },
    { tab: 'terminal',    label: t.nav.terminal,     Icon: Cpu,            minLevel: 3 },
    { tab: 'leaderboard', label: t.nav.leaderboard,  Icon: Trophy,         minLevel: 5 },
    { tab: 'market',      label: t.nav.market,       Icon: Repeat,         minLevel: 10 },
    { tab: 'feedback',    label: t.nav.feedback,     Icon: MessageSquare,  minLevel: 15 },
  ], [t]);

  return (
    <nav className="bottom-nav" aria-label="Hauptnavigation" style={{ gridTemplateColumns: `repeat(${ITEMS.length}, 1fr)` }}>
      {ITEMS.map((item) => {
        const locked = item.minLevel !== undefined && playerLevel < item.minLevel;
        const classes = [
          'bottom-nav__item',
          active === item.tab ? 'bottom-nav__item--active' : '',
          locked ? 'bottom-nav__item--locked' : '',
        ].filter(Boolean).join(' ');

        return (
          <button
            key={item.tab}
            type="button"
            className={classes}
            onClick={() => { if (!locked) onChange(item.tab); }}
            aria-pressed={active === item.tab}
            aria-disabled={locked}
            title={locked ? `Ab Level ${item.minLevel} verfügbar` : undefined}
          >
            <span className="bottom-nav__glyph" aria-hidden>
              <item.Icon size={22} strokeWidth={1.75} />
              {locked && (
                <span className="bottom-nav__lock" aria-hidden>
                  <Lock size={11} strokeWidth={2.5} />
                </span>
              )}
            </span>
            <span className="bottom-nav__label">
              {locked ? `Lvl ${item.minLevel}` : item.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

/** Exported so App.tsx can guard against routing to a tab the player
 *  hasn't unlocked yet (URL-hash jumps, stale persisted state). */
export function isTabUnlocked(tab: Tab, playerLevel: number): boolean {
  const min = MIN_LEVELS[tab];
  if (min === undefined) return true;
  return playerLevel >= min;
}
